import React, { useMemo } from 'react';
import {
    Box,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DIdAutocomplete } from '../../../../dum';
import { PlanRowProductState, ProductOption } from '../production-planning';
import { HOURS_INCREMENT } from './plan-helpers';

// One planned product: PURELY the three fields a planner types — which product,
// for how many hours, at what efficiency — and nothing else.
//
// The production figures sit beside the efficiency input so the planner can
// read the complete inventory → prediction → final story while editing the
// line. The same values feed the collapsed-row summary.
export interface PlanRowProductFigure {
    openText: string;
    tooltip: string[];
}

export function PlanTooltip({ paragraphs }: { paragraphs: string[] }) {
    return (
        <Box sx={{ maxWidth: 440 }}>
            {paragraphs.map((paragraph, index) => (
                <Typography
                    key={`${index}-${paragraph}`}
                    component={'p'}
                    variant={'body2'}
                    sx={{ m: 0, ...(index > 0 ? { mt: 1 } : {}) }}
                >
                    {paragraph}
                </Typography>
            ))}
        </Box>
    );
}

interface IPlanRowProduct {
    rowProduct: PlanRowProductState;
    index: number;
    // The machine this product runs on. Only gates the inputs — no machine, no
    // product to pick.
    machineId: number | null;
    // Products offered for picking (already narrowed to the machine's type).
    productOptions: ProductOption[];
    // Of those, the ones this machine has actually produced. Used to label the
    // picker's two sections; empty when the machine has no history (or it hasn't
    // arrived yet), in which case the picker stays ungrouped.
    producedProductIds: Set<number>;
    // Full product list, for resolving the label of an already-saved product
    // that falls outside the current filter.
    allProducts: ProductOption[];
    figure?: PlanRowProductFigure;
    canEdit: boolean;
    onChange: (index: number, patch: Partial<PlanRowProductState>) => void;
    onRemove: (index: number) => void;
}

export function PlanRowProduct({
    rowProduct,
    index,
    machineId,
    productOptions,
    producedProductIds,
    allProducts,
    figure,
    canEdit,
    onChange,
    onRemove,
}: IPlanRowProduct) {
    // A saved product may sit outside the current type filter, so it wouldn't be
    // in productOptions and DIdAutocomplete couldn't render its label. Keep the
    // pick list filtered, but append the selected product when it's missing.
    //
    // The append is then re-partitioned by section, because MUI's groupBy only
    // inserts a header when the group CHANGES between adjacent options — an
    // already-produced product tacked on at the end would print the "ya
    // producidos" header a second time at the bottom of the list.
    const options = useMemo(() => {
        const withSelected = (() => {
            if (!rowProduct.product_id) return productOptions;
            if (
                productOptions.some(
                    (product) => product.id === rowProduct.product_id,
                )
            ) {
                return productOptions;
            }
            const selected = allProducts.find(
                (product) => product.id === rowProduct.product_id,
            );
            return selected ? [...productOptions, selected] : productOptions;
        })();

        if (producedProductIds.size === 0 || withSelected === productOptions) {
            return withSelected;
        }
        // Stable: within each section the caller's most-produced-first order
        // (and the catalogue's order below it) is preserved.
        return [...withSelected].sort(
            (a, b) =>
                (producedProductIds.has(a.id) ? 0 : 1) -
                (producedProductIds.has(b.id) ? 0 : 1),
        );
    }, [productOptions, allProducts, rowProduct.product_id, producedProductIds]);

    // Section header per option. Only grouped once the machine's history has
    // arrived — with an empty set every product would sit under the second
    // header, which is a claim about the machine we can't make while the query
    // is still in flight.
    //
    // Both labels name the window on purpose: the query behind this is bounded
    // to the last 12 months (deliberately, so the ordering matches the period
    // the kg/hr rates beside it are averaged over). An unqualified "nunca
    // producidos" would be false for a product the machine ran two years ago.
    const groupProduct = useMemo(() => {
        if (producedProductIds.size === 0) return undefined;
        return (product: ProductOption) =>
            producedProductIds.has(product.id)
                ? 'Producidos en esta máquina el último año'
                : 'Sin producción en esta máquina el último año';
    }, [producedProductIds]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                width: '100%',
            }}
        >
            <Box sx={{ flex: '2 1 220px', minWidth: 200 }}>
                <DIdAutocomplete
                    id={rowProduct.product_id}
                    onChange={(productId) =>
                        onChange(index, { product_id: productId })
                    }
                    options={options}
                    groupBy={groupProduct}
                    itemText={'compound_description'}
                    label={'Producto'}
                    disabled={!canEdit || !machineId}
                    minWidth={0}
                />
            </Box>
            <TextField
                type={'number'}
                label={'Horas'}
                size={'small'}
                margin={'none'}
                value={rowProduct.hours}
                onChange={(e) => {
                    const parsed = Number(e.target.value);
                    onChange(index, {
                        hours:
                            Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
                    });
                }}
                disabled={!canEdit || !machineId}
                inputProps={{ min: 0, step: HOURS_INCREMENT }}
                sx={{ width: 90 }}
            />
            {/* How much of the machine's historical kg/hr this line is expected
                to actually hit. Sits with Producto and Horas because it belongs
                to the same sentence: this product, for these hours, at this
                pace. Bounded to (0, 100]: it is a share of a rate, and the
                backend rejects anything outside that. */}
            <Tooltip
                title={
                    'Qué parte del rendimiento histórico esperas lograr en este ' +
                    'renglón. 100% = el promedio histórico tal cual; 80% (el ' +
                    'valor por defecto) baja una predicción de 40 bultos a 32.'
                }
            >
                <TextField
                    type={'number'}
                    label={'Eficiencia %'}
                    size={'small'}
                    margin={'none'}
                    value={rowProduct.efficiency}
                    onChange={(e) => {
                        const parsed = Number(e.target.value);
                        onChange(index, {
                            efficiency: Number.isFinite(parsed) ? parsed : 0,
                        });
                    }}
                    disabled={!canEdit || !machineId}
                    inputProps={{ min: 5, max: 100, step: 5 }}
                    sx={{ width: 110 }}
                />
            </Tooltip>

            {figure ? (
                <Tooltip
                    title={<PlanTooltip paragraphs={figure.tooltip} />}
                    slotProps={{
                        tooltip: { sx: { maxWidth: 460 } },
                    }}
                >
                    <Typography
                        component={'span'}
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{
                            flex: '1 1 260px',
                            minWidth: 180,
                            lineHeight: 1.2,
                        }}
                    >
                        {figure.openText}
                    </Typography>
                </Tooltip>
            ) : null}

            {/* Right-aligns the delete button across every product line, so a
                row with three products has one column of them rather than three
                icons at three different offsets. */}
            <Box sx={{ flexGrow: 1, minWidth: 8 }} />

            {canEdit ? (
                <Tooltip title={'Quitar producto'}>
                    <span>
                        <IconButton
                            size={'small'}
                            onClick={() => onRemove(index)}
                        >
                            <DeleteOutlineIcon fontSize={'inherit'} />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}
        </Box>
    );
}
